import axios from "axios";

interface RefreshTokenResponse {
  success: boolean;
  message: string;
  data: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };
}

class TokenRefreshService {
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value: string) => void;
    reject: (error: Error) => void;
  }> = [];

  private processQueue(
    error: Error | null = null,
    token: string | null = null
  ) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else if (token) {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  async refreshToken(refreshToken: string): Promise<string> {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const baseURL =
        import.meta.env.VITE_API_URL ||
        "https://jp-core-api-dev.azurewebsites.net/api/v1";

      const response = await axios.post<RefreshTokenResponse>(
        `${baseURL}/auth/refresh-token`,
        { refreshToken },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000,
        }
      );

      if (response.data.success) {
        const { access_token, refresh_token } = response.data.data;

        // Update stored tokens in localStorage AND trigger Zustand update
        this.updateStoredTokens(access_token, refresh_token);

        // Process the queue with the new token
        this.processQueue(null, access_token);

        return access_token;
      } else {
        throw new Error(response.data.message || "Token refresh failed");
      }
    } catch (error) {
      // Don't clear tokens or redirect here - let the calling code handle that
      // This allows for retry logic in the caller
      const errorInstance =
        error instanceof Error ? error : new Error("Token refresh failed");
      this.processQueue(errorInstance, null);

      throw errorInstance;
    } finally {
      this.isRefreshing = false;
    }
  }

  private updateStoredTokens(accessToken: string, refreshToken: string) {
    try {
      const authStorage = localStorage.getItem("auth-storage");
      if (authStorage) {
        const parsedAuth = JSON.parse(authStorage);
        if (parsedAuth.state) {
          parsedAuth.state.token = accessToken;
          parsedAuth.state.refreshToken = refreshToken;
          localStorage.setItem("auth-storage", JSON.stringify(parsedAuth));

          // Dispatch a storage event to notify Zustand of the change
          // This helps sync the in-memory Zustand state with localStorage
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'auth-storage',
            newValue: JSON.stringify(parsedAuth),
          }));
        }
      }
    } catch (error) {
      console.error("Error updating stored tokens:", error);
    }
  }

  clearAuthTokens() {
    localStorage.removeItem("auth-storage");
  }

  getStoredRefreshToken(): string | null {
    try {
      const authStorage = localStorage.getItem("auth-storage");
      if (authStorage) {
        const parsedAuth = JSON.parse(authStorage);
        return parsedAuth.state?.refreshToken || null;
      }
    } catch (error) {
      console.error("Error getting stored refresh token:", error);
    }
    return null;
  }

  getStoredAccessToken(): string | null {
    try {
      const authStorage = localStorage.getItem("auth-storage");
      if (authStorage) {
        const parsedAuth = JSON.parse(authStorage);
        return parsedAuth.state?.token || null;
      }
    } catch (error) {
      console.error("Error getting stored access token:", error);
    }
    return null;
  }
}

export const tokenRefreshService = new TokenRefreshService();
